import React from 'react';
import { useTokens } from '@archieai/ui';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';
import { usePreferences } from '../../contexts/PreferencesContext';
import type { TrackerPreferences } from '../../utils/preferences';

type Brand = TrackerPreferences['global']['brand'];
type PlatformVariant = TrackerPreferences['global']['platformVariant'];

const BRAND_OPTIONS: { value: Brand; label: string; description: string }[] = [
  { value: 'martha', label: 'Martha', description: 'Default Martha palette and typography.' },
  { value: 'archie', label: 'Archie', description: 'Archie-branded palette and typography.' },
];

const PLATFORM_OPTIONS: { value: PlatformVariant; label: string; description: string }[] = [
  { value: 'web', label: 'Web', description: 'Geist font, 16px body, web-style sizing.' },
  { value: 'mac', label: 'Mac', description: 'SF Pro font, 13px body, native macOS feel.' },
  { value: 'windows', label: 'Windows', description: 'Segoe UI font, 14px body, Windows sizing.' },
];

interface ChoiceCardProps<V extends string> {
  value: V;
  label: string;
  description: string;
  selected: boolean;
  onSelect: (value: V) => void;
  name: string;
}

function ChoiceCard<V extends string>({ value, label, description, selected, onSelect, name }: ChoiceCardProps<V>) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
        ${selected
          ? 'border-coral-400 bg-coral-50/40'
          : 'border-line hover:border-line-strong hover:bg-surface-muted'}
      `}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected}
        onChange={() => onSelect(value)}
        className="mt-0.5 accent-coral-500"
      />
      <div className="flex-1">
        <div className="text-sm font-medium text-text">{label}</div>
        <div className="text-[12px] text-muted mt-0.5">{description}</div>
      </div>
    </label>
  );
}

const BrandPreview: React.FC = () => {
  const tokens = useTokens();
  const { primary, accent, font, monoFont, buttonHeight, radiusMd } = tokens;

  return (
    <div className="flex items-center gap-4 p-3 rounded-lg border border-line bg-surface" style={{ fontFamily: font }}>
      <div className="flex flex-col items-center gap-1">
        <span className="w-10 h-10" style={{ backgroundColor: primary, borderRadius: radiusMd }} title={`primary: ${primary}`} />
        <span className="text-[9px] uppercase tracking-widest text-subtle">Primary</span>
      </div>
      <div className="flex flex-col items-center gap-1">
        <span className="w-10 h-10" style={{ backgroundColor: accent, borderRadius: radiusMd }} title={`accent: ${accent}`} />
        <span className="text-[9px] uppercase tracking-widest text-subtle">Accent</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-base font-semibold text-text truncate">Aa — Heading</div>
        <div className="text-sm text-muted truncate">The quick brown fox jumps over the lazy dog.</div>
        <div className="text-[11px] text-subtle mt-1" style={{ fontFamily: monoFont }}>
          button {buttonHeight.md}px · radius {radiusMd}px
        </div>
      </div>
    </div>
  );
};

export const AppearanceSettings: React.FC = () => {
  const { preferences, setGlobalPreferences } = usePreferences();
  const { brand, platformVariant } = preferences.global;

  return (
    <div className="space-y-6">
      <Panel>
        <PanelHeader title="Brand" subtitle="Color scheme and typography" />
        <PanelBody>
          <div className="space-y-2">
            {BRAND_OPTIONS.map(opt => (
              <ChoiceCard
                key={opt.value}
                name="brand"
                value={opt.value}
                label={opt.label}
                description={opt.description}
                selected={brand === opt.value}
                onSelect={(v) => setGlobalPreferences({ brand: v })}
              />
            ))}
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader title="Platform variant" subtitle="Sizing, fonts, radii" />
        <PanelBody>
          <div className="space-y-2">
            {PLATFORM_OPTIONS.map(opt => (
              <ChoiceCard
                key={opt.value}
                name="platformVariant"
                value={opt.value}
                label={opt.label}
                description={opt.description}
                selected={platformVariant === opt.value}
                onSelect={(v) => setGlobalPreferences({ platformVariant: v })}
              />
            ))}
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader title="Live preview" accent="accent" />
        <PanelBody>
          <BrandPreview />
          <p className="text-[11px] text-subtle mt-3">
            Saved locally in this browser. Changes propagate to the entire UI immediately.
          </p>
        </PanelBody>
      </Panel>
    </div>
  );
};

export default AppearanceSettings;
